import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { Lock, Warning as WarningIcon } from "@mui/icons-material";
import detectBrowser from "../utils/detectBrowser";
import { areThirdPartyCookiesBlocked } from "../utils/cookieCheck";
import { primaryColor } from "../Theme";

const ThirdPartyCookieWarning = () => {
  const blocked = areThirdPartyCookiesBlocked();

  // Check if the app is running inside an iframe
  const isInIframe = () => {
    try {
      return window !== window.top;
    } catch (e) {
      return true; // If we can't access window.top, we're likely in an iframe
    }
  };

  // Only show warning if both conditions are met: iframe + cookies blocked
  if (!blocked || !isInIframe()) return null;
  // Detect browser
  let browserName = "";
  const googleChromeInstructions = [
    "Open chrome://settings/privacy",
    "Privacy and Security -> Cookies and other site data",
    "Select Allow third-party cookies.",
  ];
  const edgeInstructions = [
    "Open edge://settings/privacy/cookies",
    "Disable the 'Block third-party cookies' option",
  ];
  const firefoxInstructions = [
    "Open about:preferences#privacy",
    "Scroll down to Cookies and Site Data",
    "Select the cross-site tracking option",
  ];
  const safariInstructions = [
    "Open Safari > Preferences",
    "Go to Privacy tab",
    "Uncheck 'Prevent cross-site tracking'",
  ];
  const browser = detectBrowser();
  if (browser === "Chrome") {
    browserName = "Chrome";
  } else if (browser === "Edge") {
    browserName = "Edge";
  } else if (browser === "Firefox") {
    browserName = "Firefox";
  } else if (browser === "Safari") {
    browserName = "Safari";
  }

  return (
    <Dialog
      open={blocked}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="error" sx={{ fontSize: 28 }} />
          <Typography
            variant="h6"
            component="div"
            textAlign="center"
            fontWeight="600"
            m={0}
          >
            Third-Party Cookies Blocked
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Alert
          severity="error"
          sx={{ mb: 3, backgroundColor: "#ffecec !important" }}
        >
          <AlertTitle>Action Required</AlertTitle>
          <li>
            <Typography component="span" variant="body2" color="text.secondary">
              Enable Third-Party Cookies to Continue.
            </Typography>
          </li>
          <li>
            <Typography component="span" variant="body2" color="text.secondary">
              This application is being accessed in embedded mode. To ensure
              that all features, including e-prescription services, work
              correctly, third-partry cookies need to be allowed in your
              browser.
            </Typography>
          </li>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Lock sx={{ fontSize: 28, color: "warning.main" }} />
            <Typography variant="body1" gutterBottom fontWeight="400" mt={1}>
              Important Note on Privacy
            </Typography>
          </Box>
          <Box marginLeft={2}>
            <li>
              <Typography
                component="span"
                variant="body2"
                fontSize={13}
                color="text.secondary"
              >
                Cookies are only used to support core application
              </Typography>
            </li>
            <li>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                fontSize={13}
              >
                They are required for secure services such as patient management
                and e-prescribing.
              </Typography>
            </li>
            <li>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                fontSize={13}
              >
                We do not track or monitor user behaviour outside of the
                application.
              </Typography>
            </li>
          </Box>
        </Box>
        <Box>
          <Typography variant="body1" gutterBottom fontWeight="400" mt={1}>
            Browser: {browserName}
          </Typography>
          <Box marginLeft={2}>
            {browserName === "Chrome" &&
              googleChromeInstructions.map((instruction, index) => (
                <li key={index}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    fontSize={13}
                  >
                    {instruction}
                  </Typography>
                </li>
              ))}
            {browserName === "Edge" &&
              edgeInstructions.map((instruction, index) => (
                <li key={index}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    fontSize={13}
                  >
                    {instruction}
                  </Typography>
                </li>
              ))}
            {browserName === "Firefox" &&
              firefoxInstructions.map((instruction, index) => (
                <li key={index}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    fontSize={13}
                  >
                    {instruction}
                  </Typography>
                </li>
              ))}
            {browserName === "Safari" &&
              safariInstructions.map((instruction, index) => (
                <li key={index}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    fontSize={13}
                  >
                    {instruction}
                  </Typography>
                </li>
              ))}
          </Box>
        </Box>
        <Alert
          severity="info"
          sx={{
            my: 2,
            backgroundColor: primaryColor + "!important",
            color: "#fff",
            display: "flex",
            alignItems: "center",
          }}
          icon={<InfoIcon sx={{ color: "#fff !important" }} />}
        >
          <Typography variant="body2" sx={{ color: "#fff" }}>
            Please reload the page after making the changes.
          </Typography>
        </Alert>

        {/* <Divider sx={{ my: 2 }} /> */}

        {/* <Box display="flex" gap={2} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = window.location.href)}
            sx={{ borderRadius: 2 }}
          >
            Reload After Changes
          </Button>
        </Box> */}
      </DialogContent>
    </Dialog>
  );
};

export default ThirdPartyCookieWarning;
